import onetime from "@-/com.npmjs.onetime";
import type { Event } from "@sentry/core";
import type { SentryEventListener, SentryEventSummary } from "#sentry-diagnostics-types";

const getListeners = onetime((): Set<SentryEventListener> => new Set());

export function summarizeSentryEvent(event: Event): SentryEventSummary {
  const exception = event.exception?.values?.[0];
  return {
    eventId: event.event_id,
    exceptionType: exception?.type,
    exceptionValue: exception?.value,
    level: event.level,
    message: event.message,
    tags: event.tags,
    timestamp: Date.now(),
  };
}

/**
 * A Sentry event processor that mirrors every captured event (errors, messages, watchdog reports)
 * to the registered listeners and passes the event through untouched. Registered once per
 * runtime by the setup module; subscribers come and go via {@link subscribeToSentryEvents}.
 */
export function createSentryEventMirror(): (event: Event) => Event {
  return (event) => {
    const listeners = getListeners();
    if (listeners.size > 0) {
      const summary = summarizeSentryEvent(event);
      for (const listener of listeners) {
        try {
          listener(summary);
        } catch {
          // A diagnostics subscriber must never break Sentry reporting.
        }
      }
    }
    return event;
  };
}

export function subscribeToSentryEvents(listener: SentryEventListener): () => void {
  const listeners = getListeners();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
